import { useState, useCallback, useEffect, useRef } from 'react';
import { doc, setDoc, onSnapshot, collection, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import {
  RUNBOOK,
  STATUS,
  docIdForVersion,
  marketingVersion,
  effectiveStatus,
  computeProgress,
  resolveActionHref,
  deriveProdBuild,
  ACTIONS_URL,
  DEFAULT_ASC_URL,
  DEFAULT_PLAY_URL,
} from '../lib/releaseRunbook';

/**
 * Release Checklist — live runbook status for the current app-store cut.
 *
 * The build/upload pipeline is fully automated (release.yml → build-mobile.yml:
 * tag, iOS + Android builds on the self-hosted runner, IPA→App Store Connect,
 * AAB→Play internal). This page renders that runbook in order and shows each
 * step's STATUS, read from a Firestore `release_status/{version}` doc that the
 * mayor / release workflow writes. The genuinely-human steps — the two store
 * SUBMITs — get direct console links + a mark-done toggle so Chris can drive the
 * final steps from here. Everything the old checklist hand-ran (make_release.sh,
 * git tag/push, gh release, firebase deploy, upload_ios.sh) is now CI and no
 * longer appears as a command.
 *
 * The whole page is DOC-DRIVEN and REALTIME: an `onSnapshot` subscription on
 * `release_status/{version}` streams the doc, so as the mayor / release workflow
 * writes progress (automated `steps` + human `manual` marks) the page updates
 * live with no reload. Human "mark done" toggles are written straight into the
 * doc under `manual` (not localStorage) so a mark set by Chris, another device,
 * or the mayor is the single shared source of truth.
 */

const ACTOR_LABELS = {
  ci: { text: 'CI', bg: '#e3f2fd', color: '#1565c0' },
  auto: { text: 'Auto', bg: '#ede7f6', color: '#5e35b1' },
  human: { text: 'Human', bg: '#fff3e0', color: '#e65100' },
};

const STATUS_META = {
  [STATUS.DONE]: { text: 'Done', bg: '#e8f5e9', color: '#2e7d32', dot: '✓' },
  [STATUS.IN_PROGRESS]: { text: 'In progress', bg: '#e3f2fd', color: '#1565c0', dot: '◐' },
  [STATUS.FAILED]: { text: 'Failed', bg: '#ffebee', color: '#c62828', dot: '✕' },
  [STATUS.PENDING]: { text: 'Pending', bg: '#f5f5f5', color: '#9e9e9e', dot: '○' },
};

const ACTION_LABELS = { workflow: 'Workflow run ↗', asc: 'App Store Connect ↗', play: 'Play Console ↗', web: 'Web preview ↗' };

// Chris's authoring surface for the release copy, shown inline on the
// 'copy-published' step. Saved to the release_status doc under `copy`.
// Limits mirror the store fields (TestFlight "What to Test" + App Store
// "What's New" are both ~4000 chars).
const COPY_FIELDS = [
  { key: 'testflight', label: 'TestFlight — What to Test', max: 4000, placeholder: 'What should testers focus on in this build?' },
  { key: 'appstore', label: 'App Store — What’s New', max: 4000, placeholder: 'Public-facing "What’s New" for the App Store listing.' },
  { key: 'play', label: 'Play — Release notes', max: 500, placeholder: 'Play Store release notes (500-char limit).' },
];

// The three store targets the upload tracker records. `copyKey` is the copy
// field whose exact text is snapshotted when Chris marks it uploaded.
const UPLOAD_TARGETS = [
  { key: 'appstore', label: 'App Store — What’s New', copyKey: 'appstore' },
  { key: 'testflight', label: 'TestFlight — What to Test', copyKey: 'testflight' },
  { key: 'play', label: 'Play — Release notes', copyKey: 'play' },
];

const styles = {
  versionRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' },
  input: {
    padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '1rem',
    fontFamily: "'SF Mono', Monaco, Consolas, monospace", width: '180px',
  },
  select: {
    padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '1rem',
    fontFamily: "'SF Mono', Monaco, Consolas, monospace", minWidth: '140px', background: '#fff', cursor: 'pointer',
  },
  progress: { fontSize: '0.9rem', color: '#666' },
  progressBar: { height: '4px', background: '#e0e0e0', borderRadius: '2px', margin: '1rem 0 1.5rem', overflow: 'hidden' },
  progressFill: { height: '100%', background: '#2e7d32', borderRadius: '2px', transition: 'width 0.3s ease' },
  stepList: { border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  step: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.7rem 0.85rem', borderBottom: '1px solid #f0f0f0', fontSize: '0.9rem', lineHeight: 1.5 },
  index: { width: '1.5rem', flexShrink: 0, textAlign: 'right', color: '#bbb', fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem', marginTop: '2px' },
  statusDot: { width: '1.25rem', flexShrink: 0, textAlign: 'center', marginTop: '1px', fontSize: '0.95rem' },
  stepContent: { flex: 1, minWidth: 0 },
  stepLabel: { fontWeight: 500, color: '#0b1d3a' },
  badge: { display: 'inline-block', fontSize: '0.68rem', fontWeight: 600, padding: '1px 6px', borderRadius: '3px', marginLeft: '0.5rem', verticalAlign: 'middle' },
  note: { fontSize: '0.78rem', color: '#999', marginTop: '2px' },
  actions: { marginTop: '4px', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
  actionLink: { fontSize: '0.78rem', color: '#1565c0', textDecoration: 'none', fontWeight: 500 },
  markLabel: { fontSize: '0.78rem', color: '#666', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none' },
  humanPanel: { marginTop: '2rem', padding: '1rem', border: '1px solid #ffe0b2', borderRadius: '8px', background: '#fffdf7' },
  humanTitle: { fontWeight: 600, color: '#e65100', marginBottom: '0.5rem', fontSize: '0.95rem' },
  humanLinks: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  humanBtn: { fontSize: '0.85rem', padding: '0.4rem 0.8rem', background: '#fff', border: '1px solid #ffcc80', borderRadius: '6px', color: '#e65100', textDecoration: 'none', fontWeight: 500 },
  resetBtn: { fontSize: '0.8rem', padding: '4px 12px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer', color: '#999' },
  meta: { fontSize: '0.78rem', color: '#aaa', marginTop: '1rem' },
  copyEditor: { marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  copyField: { display: 'flex', flexDirection: 'column', gap: '3px' },
  copyLabelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  copyLabel: { fontSize: '0.8rem', fontWeight: 600, color: '#0b1d3a' },
  copyCount: { fontSize: '0.72rem', fontFamily: "'SF Mono', Monaco, Consolas, monospace" },
  copyArea: {
    width: '100%', minHeight: '78px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px',
    fontSize: '0.85rem', fontFamily: "'SF Mono', Monaco, Consolas, monospace", lineHeight: 1.45, resize: 'vertical', background: '#fff',
  },
  copySavedNote: { fontSize: '0.72rem', color: '#9e9e9e' },
  derive: { marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' },
  deriveInput: {
    width: '84px', padding: '0.35rem 0.5rem', border: '1px solid #ddd', borderRadius: '6px',
    fontSize: '0.85rem', fontFamily: "'SF Mono', Monaco, Consolas, monospace",
  },
  deriveArrow: { color: '#9e9e9e', fontSize: '0.85rem' },
  deriveResult: {
    fontFamily: "'SF Mono', Monaco, Consolas, monospace", fontWeight: 700, fontSize: '0.95rem',
    color: '#2e7d32', background: '#e8f5e9', padding: '0.2rem 0.55rem', borderRadius: '6px',
  },
  deriveResultEmpty: { color: '#bbb', fontFamily: "'SF Mono', Monaco, Consolas, monospace" },
  tracker: { marginTop: '2rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fafafa' },
  trackerTitle: { fontWeight: 600, color: '#0b1d3a', marginBottom: '0.25rem', fontSize: '0.95rem' },
  trackerSub: { fontSize: '0.78rem', color: '#888', marginBottom: '0.75rem' },
  trackerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.4rem 0', borderTop: '1px solid #eee' },
  trackerBtn: { fontSize: '0.8rem', padding: '0.35rem 0.7rem', background: '#0b1d3a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' },
  trackerBtnDisabled: { fontSize: '0.8rem', padding: '0.35rem 0.7rem', background: '#e0e0e0', color: '#999', border: 'none', borderRadius: '6px', cursor: 'not-allowed', whiteSpace: 'nowrap' },
  audit: { marginTop: '1rem' },
  auditItem: { border: '1px solid #e5e5e5', borderRadius: '6px', background: '#fff', padding: '0.5rem 0.6rem', marginBottom: '0.5rem' },
  auditHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '0.8rem', marginBottom: '3px' },
  auditStore: { fontWeight: 600, color: '#0b1d3a' },
  auditTime: { color: '#999', fontFamily: "'SF Mono', Monaco, Consolas, monospace", fontSize: '0.72rem' },
  auditText: {
    margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: "'SF Mono', Monaco, Consolas, monospace",
    fontSize: '0.76rem', color: '#444', maxHeight: '96px', overflow: 'auto', background: '#f7f7f7', padding: '0.4rem', borderRadius: '4px',
  },
  auditEmpty: { fontSize: '0.8rem', color: '#aaa' },
};

const BUILD_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';

export default function ReleaseChecklist() {
  const [version, setVersion] = useState('');
  const [allVersions, setAllVersions] = useState([]);
  const [statusDoc, setStatusDoc] = useState({});
  // Optimistic overlay for this device's just-toggled manual marks, so a tick
  // feels instant before the onSnapshot round-trip. Reconciled against the doc
  // as snapshots land (see the subscription below); NOT persisted locally.
  const [pendingMarks, setPendingMarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [copy, setCopy] = useState({});
  const [copySaved, setCopySaved] = useState(true);
  const copyTimer = useRef(null);
  // Copy + nightly are editable fields seeded once per version from the doc; a
  // live snapshot (which re-fires on every save) must not clobber in-progress
  // edits, so we only (re-)seed when the version key changes.
  const seededVersion = useRef(null);
  const [nightlyBuild, setNightlyBuild] = useState('');
  const nightlyTimer = useRef(null);
  const [uploads, setUploads] = useState([]);

  // On mount: subscribe to the known release-status versions (realtime), default
  // to the latest, else the build version. Reads are best-effort — a missing doc
  // / rule just leaves the runbook at "pending" (static, still useful).
  useEffect(() => {
    const q = query(collection(db, 'release_status'), orderBy('updatedAt', 'desc'), limit(20));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const versions = snap.docs.map((d) => d.data().version).filter(Boolean);
        setAllVersions(versions);
        // Only auto-pick a default the first time (before the user has chosen);
        // default to the LATEST release, keyed by marketing version.
        setVersion((cur) => cur || marketingVersion(versions[0] || BUILD_VERSION));
        setLoading(false);
      },
      () => {
        setVersion((cur) => cur || marketingVersion(BUILD_VERSION));
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  // Subscribe to the release-status doc for the selected version (realtime), so
  // automated `steps` + human `manual` marks written by the mayor / workflow
  // stream in live.
  //
  // The store copy comes from the DOC and nowhere else (ra-zcw9h). The cut agent
  // writes its draft into `release_status/{docId}.copy` via
  // .github/workflows/write-release-status.yml, and this page picks it up on
  // load — no code change, no PR, no deploy in the copy loop.
  //
  // The copy/nightly editors load from the doc ONCE per version
  // (`seededVersion`), deliberately: they are debounced-autosave inputs, so
  // re-applying every snapshot would fight Chris mid-keystroke and could undo
  // an edit that has not flushed yet. The cost is that copy written by the
  // agent while this page is already open does not appear until the page is
  // reloaded (or the version re-selected) — the runbook says so at §1.
  useEffect(() => {
    // New version selected → clear per-version device state so nothing leaks
    // across releases; the snapshot below refills it.
    setPendingMarks({});
    setUploads([]);
    if (!version) { setStatusDoc({}); seededVersion.current = null; return; }
    const id = docIdForVersion(version);
    const seedFields = (data) => {
      if (seededVersion.current === version) return;
      seededVersion.current = version;
      setCopy(data.copy || {});
      setCopySaved(true);
      setNightlyBuild(data.nightlyBuild != null ? String(data.nightlyBuild) : '');
    };
    const unsub = onSnapshot(
      doc(db, 'release_status', id),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setStatusDoc(data);
        seedFields(data);
        // Uploads are append-only; take the doc's list once it has caught up
        // with (or overtaken) this device's optimistic prepend.
        setUploads((prev) => {
          const docU = Array.isArray(data.uploads) ? data.uploads : [];
          return docU.length >= prev.length ? docU : prev;
        });
        // Drop optimistic marks the doc now agrees with — the doc is the truth.
        setPendingMarks((prev) => {
          const docManual = data.manual || {};
          const next = {};
          for (const [k, v] of Object.entries(prev)) {
            if ((docManual[k] === true) !== (v === true)) next[k] = v;
          }
          return next;
        });
      },
      () => { setStatusDoc({}); seedFields({}); },
    );
    return unsub;
  }, [version]);

  // Debounced save of the observed ASC nightly build number.
  const updateNightly = useCallback(
    (value) => {
      setNightlyBuild(value);
      if (nightlyTimer.current) clearTimeout(nightlyTimer.current);
      nightlyTimer.current = setTimeout(() => {
        if (!version) return;
        const id = docIdForVersion(version);
        setDoc(
          doc(db, 'release_status', id),
          { version, nightlyBuild: value, updatedAt: new Date().toISOString() },
          { merge: true },
        ).catch(() => {});
      }, 1200);
    },
    [version],
  );

  // Debounced save of the release copy to release_status/{version}.copy.
  const updateCopy = useCallback(
    (field, value) => {
      setCopy((prev) => ({ ...prev, [field]: value }));
      setCopySaved(false);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => {
        if (!version) return;
        const id = docIdForVersion(version);
        setDoc(
          doc(db, 'release_status', id),
          { version, copy: { [field]: value }, updatedAt: new Date().toISOString() },
          { merge: true },
        )
          .then(() => setCopySaved(true))
          .catch(() => {});
      }, 1200);
    },
    [version],
  );

  // Snapshot the current copy text for a store into the append-only audit
  // trail (release_status.uploads). Manual for now; the ra-k7xl store-copy API
  // push will append these records automatically later.
  const markUploaded = useCallback(
    (target) => {
      const text = (copy[target.copyKey] || '').trim();
      if (!text) return;
      const record = { store: target.key, label: target.label, text, at: new Date().toISOString() };
      // Base both the optimistic prepend AND the write on the freshest LOCAL
      // list, which already holds every prior record (doc-synced + optimistic).
      // Writing from statusDoc.uploads instead would drop records on rapid
      // clicks made before the onSnapshot echo lands (codex ra-an8b.2).
      const next = [record, ...uploads];
      setUploads(next);
      if (version) {
        const id = docIdForVersion(version);
        setDoc(
          doc(db, 'release_status', id),
          { version, uploads: next, updatedAt: record.at },
          { merge: true },
        ).catch(() => {});
      }
    },
    [copy, version, uploads],
  );

  const handleVersionChange = (v) => {
    setVersion(v);
  };

  // Effective manual map = the doc's shared human marks, with this device's
  // just-toggled (optimistic) marks overlaid until the snapshot confirms them.
  const manual = { ...(statusDoc.manual || {}), ...pendingMarks };

  const toggle = useCallback(
    (stepId) => {
      const nowDone = manual[stepId] !== true;
      // Optimistic overlay for instant feedback; the doc write below is the
      // durable, shared source of truth (no localStorage).
      setPendingMarks((prev) => ({ ...prev, [stepId]: nowDone }));
      if (version) {
        const id = docIdForVersion(version);
        setDoc(
          doc(db, 'release_status', id),
          { version, manual: { [stepId]: nowDone }, updatedAt: new Date().toISOString() },
          { merge: true },
        ).catch(() => {});
      }
    },
    [version, manual],
  );

  const statusInput = { automated: statusDoc.steps || {}, manual };
  const { done, total, pct } = computeProgress(statusInput);

  // Version picker options: available releases keyed by marketing version
  // (deduped, latest-first as they arrive ordered by updatedAt). Always include
  // the current selection so the <select> has a matching option even before its
  // doc streams in (or when defaulting to the build version with no docs yet).
  const selectedMv = marketingVersion(version);
  const pickerVersions = [...new Set(allVersions.map(marketingVersion).filter(Boolean))];
  if (selectedMv && !pickerVersions.includes(selectedMv)) pickerVersions.push(selectedMv);

  if (loading) {
    return (
      <>
        <PortalHeader />
        <div className="page">
          <PageHeader backTo="/admin" backLabel="Admin" title="Release Checklist" />
          <p style={{ color: '#888' }}>Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo="/admin" backLabel="Admin" title="Release Checklist" />

        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
          Live status of the app-store cut, streamed in realtime from the
          release_status doc — it updates as the mayor / release workflow writes
          progress, no reload. Build + upload are automated; the two store{' '}
          <strong>submits</strong> are Chris&apos;s manual steps — links + mark-done
          below. Marks are shared (stored in the doc), not per-browser.
        </p>

        <div style={styles.versionRow}>
          <label htmlFor="rc-version" style={{ fontWeight: 600 }}>Version:</label>
          <select
            id="rc-version"
            style={styles.select}
            value={selectedMv}
            onChange={(e) => handleVersionChange(e.target.value)}
          >
            {pickerVersions.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          <span style={styles.progress}>{done}/{total} ({pct}%)</span>
        </div>

        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${pct}%` }} />
        </div>

        <div style={styles.stepList}>
          {RUNBOOK.map((step, i) => {
            const st = effectiveStatus(step.id, statusInput);
            const sm = STATUS_META[st];
            const actor = ACTOR_LABELS[step.actor];
            const href = resolveActionHref(step, statusDoc);
            const isDone = st === STATUS.DONE;
            return (
              <div key={step.id} style={{ ...styles.step, ...(isDone ? { background: '#fafdfb' } : {}) }}>
                <span style={styles.index}>{i + 1}</span>
                <span style={{ ...styles.statusDot, color: sm.color }} title={sm.text}>{sm.dot}</span>
                <div style={styles.stepContent}>
                  <span style={{ ...styles.stepLabel, ...(isDone ? { color: '#7a8a7f' } : {}) }}>{step.label}</span>
                  <span style={{ ...styles.badge, background: actor.bg, color: actor.color }}>{actor.text}</span>
                  <span style={{ ...styles.badge, background: sm.bg, color: sm.color }}>{sm.text}</span>
                  {step.note && <div style={styles.note}>{step.note}</div>}
                  {step.id === 'build-derived' && (
                    <div style={styles.derive}>
                      <label htmlFor="rc-nightly" style={{ fontSize: '0.8rem', color: '#666' }}>
                        Latest ASC nightly build#:
                      </label>
                      <input
                        id="rc-nightly"
                        style={styles.deriveInput}
                        value={nightlyBuild}
                        inputMode="numeric"
                        placeholder="166"
                        onChange={(e) => updateNightly(e.target.value.replace(/[^0-9]/g, ''))}
                      />
                      <span style={styles.deriveArrow}>→ prod cut</span>
                      {deriveProdBuild(nightlyBuild) != null ? (
                        <span style={styles.deriveResult}>+{deriveProdBuild(nightlyBuild)}</span>
                      ) : (
                        <span style={styles.deriveResultEmpty}>+—</span>
                      )}
                    </div>
                  )}
                  {step.id === 'copy-published' && (
                    <div style={styles.copyEditor}>
                      {COPY_FIELDS.map((f) => (
                        <EditableCopyField
                          key={f.key}
                          field={f}
                          value={copy[f.key] || ''}
                          onChange={(v) => updateCopy(f.key, v)}
                        />
                      ))}
                      <div style={styles.copySavedNote}>
                        {copySaved ? 'Copy saved' : 'Saving…'} · the cut agent&apos;s draft, editable · stored per version in release_status
                      </div>
                    </div>
                  )}
                  <div style={styles.actions}>
                    {href && (
                      <a style={styles.actionLink} href={href} target="_blank" rel="noreferrer">
                        {ACTION_LABELS[step.action]}
                      </a>
                    )}
                    {step.actor === 'human' && (
                      <label style={styles.markLabel}>
                        <input type="checkbox" checked={!!manual[step.id]} onChange={() => toggle(step.id)} />
                        Mark done
                      </label>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.tracker}>
          <div style={styles.trackerTitle}>Store upload tracker</div>
          <div style={styles.trackerSub}>
            Audit trail of exactly what copy went to each store, and when. Mark uploaded to
            snapshot the current text above. The copy here is what the{' '}
            <code>push-release-notes.yml</code> workflow pushes to ASC / Play (reads
            this doc&apos;s <code>copy</code>; dry-run by default, never submits).
          </div>
          {UPLOAD_TARGETS.map((t) => {
            const hasText = !!(copy[t.copyKey] || '').trim();
            return (
              <div key={t.key} style={styles.trackerRow}>
                <span style={{ fontSize: '0.85rem', color: '#0b1d3a' }}>{t.label}</span>
                <button
                  style={hasText ? styles.trackerBtn : styles.trackerBtnDisabled}
                  disabled={!hasText}
                  onClick={() => markUploaded(t)}
                >
                  Mark as uploaded
                </button>
              </div>
            );
          })}
          <div style={styles.audit}>
            {uploads.length === 0 ? (
              <div style={styles.auditEmpty}>Nothing uploaded yet.</div>
            ) : (
              uploads.map((u, i) => (
                <div key={`${u.at}-${i}`} style={styles.auditItem}>
                  <div style={styles.auditHead}>
                    <span style={styles.auditStore}>{u.label || u.store}</span>
                    <span style={styles.auditTime}>{u.at}</span>
                  </div>
                  <pre style={styles.auditText}>{u.text}</pre>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={styles.humanPanel}>
          <div style={styles.humanTitle}>Final human steps</div>
          <div style={styles.humanLinks}>
            <a style={styles.humanBtn} href={statusDoc.ascUrl || DEFAULT_ASC_URL} target="_blank" rel="noreferrer">
              App Store Connect — submit ↗
            </a>
            <a style={styles.humanBtn} href={statusDoc.playUrl || DEFAULT_PLAY_URL} target="_blank" rel="noreferrer">
              Play Console — submit ↗
            </a>
            <a style={styles.humanBtn} href={statusDoc.workflowRunUrl || ACTIONS_URL} target="_blank" rel="noreferrer">
              Release workflow ↗
            </a>
          </div>
          <p style={{ fontSize: '0.78rem', color: '#a1887f', marginTop: '0.6rem', marginBottom: 0 }}>
            Verify shipped at the STORE, not on CI-green. Rev main only after both stores confirm upload.
          </p>
        </div>

        <div style={styles.meta}>
          {statusDoc.updatedAt
            ? `Status doc last updated ${statusDoc.updatedAt}`
            : 'No release_status doc for this version yet — steps show as pending until the release workflow writes status.'}
        </div>
      </div>
    </>
  );
}

/** One editable store-copy textarea with a live character count. */
function EditableCopyField({ field, value, onChange }) {
  const count = value.length;
  const over = count > field.max;
  return (
    <div style={styles.copyField}>
      <div style={styles.copyLabelRow}>
        <span style={styles.copyLabel}>{field.label}</span>
        <span style={{ ...styles.copyCount, color: over ? '#c62828' : '#9e9e9e', fontWeight: over ? 600 : 400 }}>
          {count}/{field.max}
        </span>
      </div>
      <textarea
        style={{ ...styles.copyArea, border: over ? '1px solid #c62828' : '1px solid #ddd' }}
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
